import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, ArrowLeft, RefreshCw, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { AnalysisResult } from "@/types/analysis";
import { ScoreOverview } from "@/components/report/ScoreOverview";
import { SEOAuditTab } from "@/components/report/SEOAuditTab";
import { AIVisibilityTab } from "@/components/report/AIVisibilityTab";
import { BacklinkTab } from "@/components/report/BacklinkTab";
import { CompetitorTab } from "@/components/report/CompetitorTab";
import { RankingTab } from "@/components/report/RankingTab";
import { ActionPlanTab } from "@/components/report/ActionPlanTab";
import { KeywordsTab } from "@/components/report/KeywordsTab";
import WaitlistBanner from "@/components/WaitlistBanner";
import WaitlistModal from "@/components/WaitlistModal";
import FeedbackBanner from "@/components/FeedbackBanner";
import FeedbackModal from "@/components/FeedbackModal";
import ReportChat from "@/components/report/ReportChat";
import { useAuth } from "@/hooks/useAuth";

const ReportPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [activeTab, setActiveTab] = useState("seo");
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const dataParam = searchParams.get("data");
    if (!dataParam) {
      navigate("/");
      return;
    }
    try {
      const decoded = JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(dataParam)))));
      setResult(decoded);
    } catch {
      navigate("/");
    }
  }, [searchParams]);

  if (!result) return null;

  return (
    <div className="min-h-screen bg-background">
      {!user && (
        <nav className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2">
                <img src="/Unranked-agency-logo.png" alt="Unranked Agency" className="h-6 sm:h-7 w-auto object-contain max-w-[140px] sm:max-w-none" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-muted-foreground hidden sm:block truncate max-w-xs">
                {result.url}
              </div>
              {searchParams.get("email") && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={() => navigate(`/analyze?url=${encodeURIComponent(result.url)}&email=${encodeURIComponent(searchParams.get("email")!)}`)}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Re-analyze
                </Button>
              )}
            </div>
          </div>
        </nav>
      )}

      <div className="container mx-auto px-6 py-8">
        <ScoreOverview result={result} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
          <TabsList className="w-full justify-start bg-card border border-border h-auto p-1 flex-wrap gap-1">
            <TabsTrigger value="seo" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">SEO Audit</TabsTrigger>
            <TabsTrigger value="keywords" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Keywords</TabsTrigger>
            <TabsTrigger value="ai" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">AI Visibility</TabsTrigger>
            <TabsTrigger value="backlinks" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Link Intelligence</TabsTrigger>
            <TabsTrigger value="competitors" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Competitors</TabsTrigger>
            <TabsTrigger value="ranking" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Ranking</TabsTrigger>
            <TabsTrigger value="actions" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Action Plan</TabsTrigger>
          </TabsList>

          <TabsContent value="seo"><SEOAuditTab data={result.seoAudit} onViewActionPlan={() => setActiveTab("actions")} /></TabsContent>
          <TabsContent value="keywords"><KeywordsTab data={result.keywordAnalysis} /></TabsContent>
          <TabsContent value="ai"><AIVisibilityTab data={result.aiVisibility} backlinkScore={result.backlinkProfile.overallScore} /></TabsContent>
          <TabsContent value="backlinks"><BacklinkTab data={result.backlinkProfile} linkIntelligence={result.linkIntelligence} /></TabsContent>
          <TabsContent value="competitors"><CompetitorTab data={result.competitorIntelligence} analyzedUrl={result.url} analyzedScores={{
            contentDepth: result.seoAudit.contentQuality.score,
            keywordCoverage: result.keywordAnalysis?.overallScore ?? 0,
            technicalSEO: result.seoAudit.technical.score,
            backlinkStrength: result.backlinkProfile.overallScore,
            aiVisibility: result.aiVisibility.overallScore,
          }} /></TabsContent>
          <TabsContent value="ranking"><RankingTab data={result.rankingPrediction} /></TabsContent>
          <TabsContent value="actions"><ActionPlanTab data={result.actionPlan} /></TabsContent>
        </Tabs>

        <WaitlistBanner variant="compact" onJoinClick={() => setShowWaitlist(true)} />
        <FeedbackBanner onFeedbackClick={() => setShowFeedback(true)} />
      </div>

      <WaitlistModal open={showWaitlist} onOpenChange={setShowWaitlist} />
      <FeedbackModal open={showFeedback} onOpenChange={setShowFeedback} defaultEmail={searchParams.get("email") || ""} />
      {searchParams.get("email") === "stepanruzicka14@winchendon.org" ? (
        <ReportChat analysisData={result} />
      ) : (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-1">
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0.5 mb-1">Coming Soon</Badge>
          <button
            disabled
            className="w-14 h-14 rounded-full bg-primary/40 text-primary-foreground/60 flex items-center justify-center shadow-lg cursor-not-allowed opacity-60"
            aria-label="Ask AI (Coming Soon)"
          >
            <MessageSquare className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
